import { Injectable } from '@angular/core';
import {
  FRAME_AUTH_APP,
  FRAME_AUTH_EXPIRED,
  FRAME_AUTH_HELLO,
  HELLO_TIMEOUT_MS,
  TOKEN_WAIT_MS,
  isEmbeddedFrame,
  isFrameAuthLogout,
  isFrameAuthToken,
  isLegacyAuthString,
  isTrustedOnclassOrigin,
  readAccessCookie,
  resolveParentOrigin,
  stripBearer,
} from './frame-auth';

export type AccessChangedHandler = (access: string) => void;
export type LogoutHandler = () => void;

@Injectable({ providedIn: 'root' })
export class FrameAuthService {
  private memoryAccess: string | null = null;
  private handshakeDone = false;
  private started = false;
  private iframe = false;
  private parentOrigin = '';
  private userId?: string;
  private lang?: string;
  private refreshWait: Promise<string | null> | null = null;
  private tokenWaiters: Array<(token: string | null) => void> = [];
  private accessChangedHandlers: AccessChangedHandler[] = [];
  private logoutHandlers: LogoutHandler[] = [];
  private messageListener?: (event: MessageEvent) => void;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.iframe = isEmbeddedFrame();
    this.parentOrigin = this.iframe ? resolveParentOrigin() : '';

    if (!this.iframe) {
      return;
    }

    this.messageListener = (event) => this.onMessage(event);
    window.addEventListener('message', this.messageListener);
    this.postToParent({ type: FRAME_AUTH_HELLO, app: FRAME_AUTH_APP });
  }

  stop(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
    this.resolveTokenWaiters(null);
    this.tokenWaiters = [];
    this.accessChangedHandlers = [];
    this.logoutHandlers = [];
    this.refreshWait = null;
    this.started = false;
  }

  async ready(): Promise<void> {
    this.start();
    if (!this.iframe || this.getAccess()) {
      return;
    }
    await Promise.race([this.waitForToken(), this.delay(HELLO_TIMEOUT_MS)]);
  }

  getAccess(): string | null {
    if (this.memoryAccess) {
      return this.memoryAccess;
    }
    if (this.handshakeDone) {
      return null;
    }
    return readAccessCookie();
  }

  getParentOrigin(): string {
    return this.parentOrigin;
  }

  isInIframe(): boolean {
    return this.iframe;
  }

  onAccessChanged(handler: AccessChangedHandler): void {
    this.accessChangedHandlers.push(handler);
  }

  onLogout(handler: LogoutHandler): void {
    this.logoutHandlers.push(handler);
  }

  postAppMessage(type: string, data?: unknown): void {
    this.postToParent({ source: FRAME_AUTH_APP, type, data });
  }

  requestRefreshedAccess(): Promise<string | null> {
    if (!this.iframe) {
      return Promise.resolve(null);
    }
    if (this.refreshWait) {
      return this.refreshWait;
    }
    this.postToParent({ type: FRAME_AUTH_EXPIRED, app: FRAME_AUTH_APP });
    this.refreshWait = this.waitForToken(TOKEN_WAIT_MS).finally(() => {
      this.refreshWait = null;
    });
    return this.refreshWait;
  }

  private onMessage(event: MessageEvent): void {
    if (!isTrustedOnclassOrigin(event.origin)) {
      return;
    }
    if (isLegacyAuthString(event.data)) {
      return;
    }
    if (isFrameAuthToken(event.data)) {
      this.applyToken(event.data.access, event.data.userId, event.data.lang);
      return;
    }
    if (isFrameAuthLogout(event.data)) {
      this.clearAccess();
      this.logoutHandlers.forEach((handler) => handler());
    }
  }

  private applyToken(access: unknown, userId?: unknown, lang?: unknown): void {
    if (typeof access !== 'string' || !access) {
      return;
    }
    const next = stripBearer(access);
    if (!next) {
      return;
    }
    const current = this.getAccess();
    const previous = current ? stripBearer(current) : null;
    this.memoryAccess = access;
    this.handshakeDone = true;
    if (typeof userId === 'string' && userId && !this.userId) {
      this.userId = userId;
    }
    if (typeof lang === 'string' && lang && !this.lang) {
      this.lang = lang;
    }
    this.resolveTokenWaiters(access);
    if (previous !== next) {
      this.accessChangedHandlers.forEach((handler) => handler(access));
    }
  }

  private clearAccess(): void {
    this.memoryAccess = null;
    this.handshakeDone = true;
    this.resolveTokenWaiters(null);
  }

  private postToParent(payload: unknown): void {
    const origin = this.parentOrigin;
    if (!origin || origin === '*') {
      return;
    }
    try {
      window.parent?.postMessage(payload, origin);
    } catch {
      /* parent not ready */
    }
  }

  private waitForToken(timeoutMs?: number): Promise<string | null> {
    return new Promise((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish = (token: string | null) => {
        if (timer) {
          clearTimeout(timer);
        }
        resolve(token);
      };
      this.tokenWaiters.push(finish);
      if (timeoutMs) {
        timer = setTimeout(() => {
          this.tokenWaiters = this.tokenWaiters.filter((waiter) => waiter !== finish);
          resolve(null);
        }, timeoutMs);
      }
    });
  }

  private resolveTokenWaiters(token: string | null): void {
    const waiters = this.tokenWaiters;
    this.tokenWaiters = [];
    waiters.forEach((waiter) => waiter(token));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
